import { useMemo } from "react";
import { useUser } from "../../context/UserContext";
import useGetPowerMenu, { PowerMenuBlock } from "./useGetPowerMenu";
import { RouteType } from "../../routes/routes";
import LoadingMenuItem from "../../components/LoadingMenuItem";
import { canSeeReport, canSeeReporting, useRights } from "../../rights";

const BLOCK_ICONS: Record<string, string> = {
    chiffres_cles: "bx bx-trending-up",
    activite: "bx bx-bar-chart-square",
    animation: "bx bx-envelope",
    base_client: "bx bx-group",
    fidelite: "bx bx-heart",
    dashboard: "bx bx-dashboard",
};

const getIconForBlock = (blockKey: string): string => {
    return BLOCK_ICONS[blockKey] || "bx bx-bar-chart";
};

const useGetDynamicReportingMenu = (): {
    reportingMenuItems: RouteType;
    // Droits PowerBI BeOne de l'utilisateur sur la section (même règle que l'API)
    canSeeReportingSection: boolean;
    // Vrai quand droits et configuration PowerBI sont chargés : canSeeReportingSection est alors fiable
    accessChecked: boolean;
    loading: boolean;
    error: string;
    refetch: () => void;
} => {
    const { selectedWorkspace } = useUser();
    const { rights, ready: rightsReady } = useRights();
    const workspaceName = selectedWorkspace?.volumeName || '';
    const { data: powerMenuData, loading, error, refetch } = useGetPowerMenu({ 
        shouldFetch: !!workspaceName, 
        workspace: workspaceName 
    });

    const reportingMenuItems = useMemo<RouteType>(() => {
        const baseMenu: RouteType = {
            name: "Reporting DataViz",
            icon: "bx bx-bar-chart",
            children: [],
            isLoading: false,
        };

        if (loading || !selectedWorkspace?.volumeName) {
            return {
                ...baseMenu,
                children: [{ name: "Chargement...", component: LoadingMenuItem }],
                isLoading: true,
            };
        }

        if (!powerMenuData) return baseMenu;

        const workspaceNameLower = selectedWorkspace.volumeName.toLowerCase();
        const { blocks, reportsLabels, powerBiParams, powerBiReports } = powerMenuData;
        const workspaceParams = powerBiParams[workspaceNameLower];

        if (!workspaceParams) {
            console.warn(`No Power BI params found for workspace: ${workspaceNameLower}`);
            return baseMenu;
        }

        // Section ouverte au client avec da.powerbi.show, ou équipe interne (da.powerbi.show.internal)
        if (!canSeeReporting(rights, workspaceParams)) {
            return baseMenu;
        }

        const workspaceReports = powerBiReports[workspaceNameLower];
        if (!workspaceReports) {
            console.warn(`No reports found for workspace: ${workspaceNameLower}`);
            return baseMenu;
        }

        const children = Object.entries(blocks)
            .map(([blockKey, blockInfo]) => {
                // Chaque rapport demande da.powerbi.report_<clé> (BeOne : _primary_menu.php:502)
                const reports = workspaceReports[blockKey]?.filter(reportKey => canSeeReport(rights, reportKey));
                if (!reports?.length) return null;

                return {
                    name: (blockInfo as PowerMenuBlock).label,
                    icon: getIconForBlock(blockKey),
                    children: reports.map((reportKey) => ({
                        path: `/reporting/${blockKey}/${reportKey}`,
                        name: reportsLabels[reportKey] || reportKey,
                    })),
                };
            })
            .filter(Boolean) as RouteType[];

        return children.length > 0 ? { ...baseMenu, children } : baseMenu;
    }, [powerMenuData, selectedWorkspace, loading, rights]);

    const canSeeReportingSection = !!powerMenuData && !!selectedWorkspace
        && canSeeReporting(rights, powerMenuData.powerBiParams[selectedWorkspace.volumeName.toLowerCase()]);

    const accessChecked = rightsReady && !!powerMenuData && !!selectedWorkspace;

    return { reportingMenuItems, canSeeReportingSection, accessChecked, loading, error, refetch };
};

export default useGetDynamicReportingMenu;
